import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { GripHorizontal, Maximize2, MessageSquare, Mic, MicOff, Minimize2 } from 'lucide-react'
import { VoiceAgentFace } from '@/components/voice/VoiceAgentFace'
import type { AssistantStatus, ConnectionMode } from '@/lib/voice-console/state/types'
import { cn } from '@/lib/utils'

export interface FloatingWidgetPosition {
  x: number
  y: number
}

interface FloatingAssistantWidgetProps {
  agentName: string
  assistantStatus: AssistantStatus
  unreadActivity: boolean
  expanded: boolean
  isMobile: boolean
  isConnected: boolean
  connectionMode: ConnectionMode | null
  isMicMuted: boolean
  position: FloatingWidgetPosition
  surfaceMode: 'fullscreen' | 'widget'
  fullscreenStyle?: CSSProperties
  getInputVolume?: () => number
  getOutputFrequencyData?: () => Uint8Array | null
  onPositionChange: (position: FloatingWidgetPosition) => void
  onExpandChange: (expanded: boolean) => void
  onRestoreFullscreen: () => void
  onVoiceAction: () => void
  onTextAction: () => void
  children: ReactNode
}

const VIEWPORT_PADDING = 12
const COLLAPSED_WIDGET_WIDTH = 292
const COLLAPSED_WIDGET_HEIGHT = 188
const MOBILE_COLLAPSED_WIDGET_HEIGHT = 72
const EXPANDED_WIDGET_WIDTH = 440
const EXPANDED_WIDGET_HEIGHT = 716

function getWidgetBounds(expanded: boolean) {
  return expanded
    ? { width: EXPANDED_WIDGET_WIDTH, height: EXPANDED_WIDGET_HEIGHT }
    : { width: COLLAPSED_WIDGET_WIDTH, height: COLLAPSED_WIDGET_HEIGHT }
}

export function clampFloatingWidgetPosition(
  position: FloatingWidgetPosition,
  bounds = getWidgetBounds(false)
): FloatingWidgetPosition {
  const width = window.innerWidth
  const height = window.innerHeight
  return {
    x: Math.min(Math.max(position.x, VIEWPORT_PADDING), Math.max(VIEWPORT_PADDING, width - (bounds.width + VIEWPORT_PADDING))),
    y: Math.min(Math.max(position.y, VIEWPORT_PADDING), Math.max(VIEWPORT_PADDING, height - bounds.height)),
  }
}

function clampMobileWidgetPosition(position: FloatingWidgetPosition): FloatingWidgetPosition {
  const height = window.innerHeight
  return {
    x: VIEWPORT_PADDING,
    y: Math.min(
      Math.max(position.y, VIEWPORT_PADDING),
      Math.max(VIEWPORT_PADDING, height - (MOBILE_COLLAPSED_WIDGET_HEIGHT + VIEWPORT_PADDING))
    ),
  }
}

function statusAccent(status: AssistantStatus): string {
  switch (status) {
    case 'speaking':
      return 'bg-pink-400'
    case 'tool_use':
      return 'bg-violet-400'
    case 'processing':
    case 'connecting':
      return 'bg-amber-400'
    case 'error':
      return 'bg-red-500'
    default:
      return 'bg-cyan-400'
  }
}

function statusLabel(status: AssistantStatus): string {
  switch (status) {
    case 'connecting':
      return 'Connecting'
    case 'listening':
      return 'Listening'
    case 'processing':
      return 'Processing'
    case 'tool_use':
      return 'Working'
    case 'speaking':
      return 'Speaking'
    case 'error':
      return 'Error'
    case 'disconnected':
      return 'Offline'
    default:
      return 'Ready'
  }
}

function WaveformIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="4" y1="15" x2="4" y2="9" />
      <line x1="8" y1="18" x2="8" y2="6" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="16" y1="18" x2="16" y2="6" />
      <line x1="20" y1="15" x2="20" y2="9" />
    </svg>
  )
}

export function FloatingAssistantWidget({
  agentName,
  assistantStatus,
  unreadActivity,
  expanded,
  isMobile,
  isConnected,
  connectionMode,
  isMicMuted,
  position,
  surfaceMode,
  fullscreenStyle,
  getInputVolume,
  getOutputFrequencyData,
  onPositionChange,
  onExpandChange,
  onRestoreFullscreen,
  onVoiceAction,
  onTextAction,
  children,
}: FloatingAssistantWidgetProps) {
  const [mounted, setMounted] = useState(false)
  const dragStateRef = useRef<{
    pointerId: number
    offsetX: number
    offsetY: number
    axis: 'free' | 'vertical'
    didDrag: boolean
  } | null>(null)
  const suppressClickRef = useRef(false)
  const widgetVoiceRingRef = useRef<HTMLSpanElement>(null)
  const volumeSmoothRef = useRef(0)
  const volumeRafRef = useRef<number>()

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted) return

    const clampedPosition = isMobile
      ? clampMobileWidgetPosition(position)
      : clampFloatingWidgetPosition(position, getWidgetBounds(expanded))
    if (clampedPosition.x !== position.x || clampedPosition.y !== position.y) {
      onPositionChange(clampedPosition)
    }

    const handleResize = () => {
      const nextPosition = isMobile
        ? clampMobileWidgetPosition(position)
        : clampFloatingWidgetPosition(position, getWidgetBounds(expanded))
      if (nextPosition.x !== position.x || nextPosition.y !== position.y) {
        onPositionChange(nextPosition)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [expanded, isMobile, mounted, onPositionChange, position])

  useEffect(() => {
    const ring = widgetVoiceRingRef.current
    if (!ring) return

    const active = isConnected && connectionMode === 'voice' && !isMicMuted && !!getInputVolume

    if (!active) {
      ring.style.opacity = '0'
      ring.style.transform = 'scale(1)'
      ring.style.boxShadow = 'none'
      volumeSmoothRef.current = 0
      if (volumeRafRef.current) cancelAnimationFrame(volumeRafRef.current)
      return
    }

    const tick = () => {
      const raw = Math.max(0, getInputVolume())
      const boosted = Math.min(1, raw * 14)
      volumeSmoothRef.current += (boosted - volumeSmoothRef.current) * 0.22
      const intensity = volumeSmoothRef.current
      ring.style.transform = `scale(${0.96 + intensity * 0.22})`
      ring.style.opacity = String(0.14 + intensity * 0.62)
      ring.style.boxShadow = `0 0 ${8 + intensity * 12}px rgba(34, 211, 238, ${0.08 + intensity * 0.22})`
      volumeRafRef.current = requestAnimationFrame(tick)
    }

    volumeRafRef.current = requestAnimationFrame(tick)
    return () => {
      if (volumeRafRef.current) cancelAnimationFrame(volumeRafRef.current)
    }
  }, [connectionMode, getInputVolume, isConnected, isMicMuted])

  const bubbleLabel = useMemo(() => {
    const compactName = agentName.trim() || 'Assistant'
    return compactName.length > 18 ? `${compactName.slice(0, 18)}…` : compactName
  }, [agentName])

  const startDrag = (event: PointerEvent<HTMLElement>, axis: 'free' | 'vertical' = 'free') => {
    const currentTarget = event.currentTarget
    currentTarget.setPointerCapture(event.pointerId)
    suppressClickRef.current = false
    dragStateRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - position.x,
      offsetY: event.clientY - position.y,
      axis,
      didDrag: false,
    }
  }

  const onDrag = (event: PointerEvent<HTMLElement>) => {
    if (!dragStateRef.current || dragStateRef.current.pointerId !== event.pointerId) return
    const nextX = event.clientX - dragStateRef.current.offsetX
    const nextY = event.clientY - dragStateRef.current.offsetY
    if (!dragStateRef.current.didDrag) {
      const movedX = Math.abs(nextX - position.x)
      const movedY = Math.abs(nextY - position.y)
      if (movedX > 4 || movedY > 4) {
        dragStateRef.current.didDrag = true
      }
    }

    onPositionChange(
      dragStateRef.current.axis === 'vertical'
        ? clampMobileWidgetPosition({
            x: position.x,
            y: nextY,
          })
        : clampFloatingWidgetPosition({
            x: nextX,
            y: nextY,
          }, getWidgetBounds(expanded))
    )
  }

  const endDrag = (event: PointerEvent<HTMLElement>) => {
    if (!dragStateRef.current || dragStateRef.current.pointerId !== event.pointerId) return
    if (dragStateRef.current.didDrag) {
      suppressClickRef.current = true
    }
    dragStateRef.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  if (!mounted) {
    return null
  }

  const isFullscreen = surfaceMode === 'fullscreen'
  const showExpandedCard = !isMobile && expanded
  const runtimePanelVisible = isFullscreen || showExpandedCard
  const showVoiceAsMuted = isConnected && connectionMode === 'voice' && isMicMuted
  const showMobileTextButton = !isConnected || connectionMode !== 'chat'
  const voiceButtonLabel = !isConnected ? 'Voice' : connectionMode === 'voice' ? (isMicMuted ? 'Unmute' : 'Mute') : 'Switch Voice'
  const textButtonLabel = !isConnected ? 'Text' : connectionMode === 'chat' ? 'Open Chat' : 'Switch Text'
  const voiceButtonToneClassName = !isConnected
    ? 'border-violet-700/60 bg-violet-950/70 text-violet-200 hover:bg-violet-900/75'
    : connectionMode === 'voice'
      ? showVoiceAsMuted
        ? 'border-red-800 bg-red-950/60 text-red-300 hover:bg-red-900/60'
        : 'border-slate-700 bg-slate-900/90 text-slate-100 hover:bg-slate-800/90'
      : 'border-slate-700 bg-slate-900/90 text-slate-300 hover:bg-slate-800/90 hover:text-cyan-200'
  const textButtonToneClassName = !isConnected
    ? 'border-cyan-700/60 bg-cyan-950/70 text-cyan-200 hover:bg-cyan-900/75'
    : connectionMode === 'chat'
      ? 'border-slate-700 bg-slate-900/90 text-slate-100'
      : 'border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800'
  const voiceButtonIcon = !isConnected
    ? <Mic className="h-4 w-4" />
    : connectionMode === 'chat'
      ? <WaveformIcon className="h-4 w-4" />
      : showVoiceAsMuted
        ? <MicOff className="h-4 w-4" />
        : <Mic className="h-4 w-4" />
  const handleHeaderPointerDown = (event: PointerEvent<HTMLElement>) => {
    event.preventDefault()
    startDrag(event)
  }

  const stopHeaderInteraction = (event: PointerEvent<HTMLElement>) => {
    event.stopPropagation()
  }

  const runtimePanelStyle = isFullscreen
    ? fullscreenStyle
    : isMobile
      ? { left: VIEWPORT_PADDING, right: VIEWPORT_PADDING, top: clampMobileWidgetPosition(position).y }
      : { left: position.x, top: position.y, width: EXPANDED_WIDGET_WIDTH }

  const runtimePanelClassName = cn(
    'absolute transition-all duration-200',
    isFullscreen
      ? 'inset-0 pointer-events-auto opacity-100'
      : runtimePanelVisible
        ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
        : 'pointer-events-none translate-y-2 scale-95 opacity-0'
  )

  const widget = (
    <div className="pointer-events-none fixed inset-0 z-[80]">
      <div className={runtimePanelClassName} style={runtimePanelStyle}>
        <div
          className={cn(
            'overflow-hidden bg-slate-950',
            isFullscreen
              ? 'h-full w-full'
              : 'rounded-[28px] border border-slate-800/80 shadow-[0_24px_80px_rgba(15,23,42,0.55)]'
          )}
        >
          {!isFullscreen && !isMobile && (
            <div
              className="flex cursor-grab select-none items-center justify-between border-b border-slate-800 bg-slate-900/90 px-4 py-2 text-slate-400 active:cursor-grabbing"
              onPointerDown={handleHeaderPointerDown}
              onPointerMove={onDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em]">
                <GripHorizontal className="h-4 w-4" />
                <span>Floating Assistant</span>
              </div>
              <button
                type="button"
                onPointerDown={stopHeaderInteraction}
                onClick={() => onExpandChange(false)}
                className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
                aria-label="Collapse widget"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
            </div>
          )}
          <div className={cn(isFullscreen ? 'h-full w-full' : 'h-[680px] max-h-[calc(100vh-40px)]')}>
            {children}
          </div>
        </div>
      </div>

      {!isFullscreen && (isMobile ? (
        <div
          className="pointer-events-none absolute left-3 right-3"
          style={{ top: clampMobileWidgetPosition(position).y }}
        >
          <div className="pointer-events-auto">
            <div
              className="flex w-full touch-none select-none items-center gap-3 rounded-full border border-slate-800 bg-slate-950/95 px-3 py-2.5 text-left shadow-[0_18px_48px_rgba(15,23,42,0.45)] backdrop-blur-xl transition-all hover:border-slate-700 hover:bg-slate-900"
              onPointerDown={(event) => startDrag(event, 'vertical')}
              onPointerMove={onDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <button
                type="button"
                className="group flex min-w-0 flex-1 items-center gap-3 rounded-full border-0 bg-slate-950/95 text-left outline-none"
                style={{ appearance: 'none', WebkitAppearance: 'none' }}
                onClick={(event) => {
                  if (suppressClickRef.current) {
                    suppressClickRef.current = false
                    event.preventDefault()
                    event.stopPropagation()
                    return
                  }
                  onRestoreFullscreen()
                }}
              >
                <div className="relative flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-cyan-300">
                  <div className="absolute inset-[7px] overflow-hidden rounded-full">
                    <VoiceAgentFace
                      isConnected={isConnected || assistantStatus === 'connecting'}
                      isSpeaking={assistantStatus === 'speaking'}
                      status={isConnected ? 'connected' : assistantStatus}
                      getFrequencyData={getOutputFrequencyData ?? (() => null)}
                    />
                  </div>
                  <span className={cn('absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full', statusAccent(assistantStatus))} />
                  {unreadActivity && (
                    <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-slate-950" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-slate-100">{bubbleLabel}</div>
                  <div className="text-xs text-slate-400">Tap to return to fullscreen</div>
                </div>
              </button>
              <div className="flex shrink-0 items-center gap-2">
                <div className="relative flex items-center justify-center">
                  <span
                    ref={widgetVoiceRingRef}
                    className="absolute -inset-1 rounded-full border-2 border-cyan-400/80 pointer-events-none"
                    style={{ opacity: 0, willChange: 'transform, opacity, box-shadow' }}
                  />
                  <button
                    type="button"
                    onPointerDown={stopHeaderInteraction}
                    onClick={(event) => {
                      event.stopPropagation()
                      onVoiceAction()
                    }}
                    className={cn(
                      'relative inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors',
                      voiceButtonToneClassName
                    )}
                    aria-label={voiceButtonLabel}
                    title={voiceButtonLabel}
                  >
                    {voiceButtonIcon}
                  </button>
                </div>
                {showMobileTextButton && (
                  <button
                    type="button"
                    onPointerDown={stopHeaderInteraction}
                    onClick={(event) => {
                      event.stopPropagation()
                      onTextAction()
                    }}
                    className={cn(
                      'inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors',
                      connectionMode === 'chat'
                        ? 'border-slate-700 bg-slate-900/90 text-slate-100'
                        : 'border-slate-700 bg-slate-900 text-slate-100'
                    )}
                    aria-label={textButtonLabel}
                    title={textButtonLabel}
                  >
                    <MessageSquare className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="absolute pointer-events-auto"
          style={{ left: position.x, top: position.y, width: COLLAPSED_WIDGET_WIDTH }}
        >
          {!showExpandedCard && (
            <div className="overflow-hidden rounded-[26px] border border-slate-800/90 bg-slate-950/95 shadow-[0_22px_60px_rgba(15,23,42,0.5)] backdrop-blur-xl">
              <div
                className="flex cursor-grab select-none items-center justify-between border-b border-slate-800 bg-slate-900/90 px-3 py-2 text-slate-400 active:cursor-grabbing"
                onPointerDown={handleHeaderPointerDown}
                onPointerMove={onDrag}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
              >
                <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.24em]">
                  <GripHorizontal className="h-4 w-4" />
                  <span>Live Assistant</span>
                </div>
                <button
                  type="button"
                  onPointerDown={stopHeaderInteraction}
                  onClick={onRestoreFullscreen}
                  className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
                  aria-label="Restore fullscreen"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>

              <button
                type="button"
                className="w-full border-0 bg-slate-950/95 px-3 pb-3 pt-3 text-left outline-none transition-colors hover:bg-slate-900/90"
                style={{ appearance: 'none', WebkitAppearance: 'none' }}
                onClick={() => onExpandChange(true)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-100">{bubbleLabel}</div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                      <span className={cn('h-2.5 w-2.5 rounded-full', statusAccent(assistantStatus))} />
                      <span>{statusLabel(assistantStatus)}</span>
                      {connectionMode && (
                        <span className="rounded-full border border-slate-800 px-2 py-0.5 uppercase tracking-[0.18em] text-[10px] text-slate-500">
                          {connectionMode}
                        </span>
                      )}
                    </div>
                  </div>
                  {unreadActivity && (
                    <span className="rounded-full bg-emerald-400/15 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-emerald-300">
                      New
                    </span>
                  )}
                </div>

                {connectionMode === 'chat' ? (
                  <div className="mt-3 flex h-11 items-center gap-2 rounded-2xl border border-slate-800/80 bg-slate-900/75 px-3 text-slate-300">
                    <MessageSquare className="h-4 w-4 text-slate-400" />
                    <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-300">Text Mode</span>
                  </div>
                ) : (
                  <div className="mt-3 h-11 overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-900/75 px-2">
                    <VoiceAgentFace
                      isConnected={isConnected || assistantStatus === 'connecting'}
                      isSpeaking={assistantStatus === 'speaking'}
                      status={isConnected ? 'connected' : assistantStatus}
                      getFrequencyData={getOutputFrequencyData ?? (() => null)}
                    />
                  </div>
                )}
              </button>

              <div className="grid grid-cols-2 gap-2 border-t border-slate-800 px-3 py-3">
                <div className="relative flex items-center justify-center">
                  <span
                    ref={widgetVoiceRingRef}
                    className="absolute -inset-1 rounded-2xl border-2 border-cyan-400/80 pointer-events-none"
                    style={{ opacity: 0, willChange: 'transform, opacity, box-shadow' }}
                  />
                  <button
                    type="button"
                    onClick={onVoiceAction}
                    className={cn(
                      'relative inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-medium transition-colors',
                      voiceButtonToneClassName
                    )}
                  >
                    {voiceButtonIcon}
                    <span>{voiceButtonLabel}</span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={onTextAction}
                  className={cn(
                    'inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-medium transition-colors',
                    textButtonToneClassName
                  )}
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>{textButtonLabel}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )

  return createPortal(widget, document.body)
}
